import React, { useState } from 'react';
import {
  ModalFooter,
  Button,
  HStack,
  Spacer,
  Icon,
  RemoveAlertDialog,
  useDisclosure,
  Tooltip,
} from '@wegroup/design-system';
import { useTranslation } from 'react-i18next';
import { InsurancePolicyModalProps } from '../../hooks/useInsurancePolicyModal';
import useAddInsuranceModalContext from '../../hooks/useAddInsuranceModalContext';

interface Props {
  onSubmit: () => void;
  isDisabled: boolean;
  insuranceModalProps: InsurancePolicyModalProps;
}

const AddInsuranceModalCoverageFooter: React.FC<Props> = ({
  onSubmit,
  isDisabled,
  insuranceModalProps,
}) => {
  const { riskObjects, isCoverageEditOpen, setIsCoverageEditOpen } =
    useAddInsuranceModalContext();
  const [alertCallback, setAlertCallback] = useState<{
    callback: () => void;
  }>();
  const { page, setPage } = insuranceModalProps;
  const { isOpen, onClose, onOpen } = useDisclosure();
  const { t } = useTranslation();

  const isLastRiskObject = page.index === riskObjects.length - 1;

  const handlePrevious = () => {
    setPage({ type: 'COVERAGE', index: page.index! - 1 });
  };

  const handleSubmit = () => {
    if (isLastRiskObject) {
      onSubmit();
    } else {
      setPage({ type: 'COVERAGE', index: page.index! + 1 });
    }
  };

  const handleClick = ({ callback }: { callback: () => void }) => {
    if (isCoverageEditOpen) {
      setAlertCallback({ callback });
      onOpen();
    } else {
      setIsCoverageEditOpen.off();
      callback();
    }
  };

  return (
    <ModalFooter as={HStack}>
      <RemoveAlertDialog
        title={t('ARE-YOU-SURE')}
        description={t(
          'ADVISORY-REPORTS_CREATION-FLOW_CURRENT-INSURANCE_MODAL_EDITING-ALERT',
        )}
        isOpen={isOpen}
        onClose={onClose}
        onConfirm={() => {
          alertCallback?.callback();
          setIsCoverageEditOpen.off();
        }}
      />
      <Button
        leftIcon={<Icon name="GeneralActionsArrowLeft" />}
        variant="white"
        onClick={() => setPage({ type: 'INSURANCE' })}
      >
        {t(
          'ADVISORY-REPORTS_CREATION-FLOW_CURRENT-INSURANCE_MODAL_BACK-TO-INSURANCE',
        )}
      </Button>
      <Spacer />
      {page.index !== 0 && (
        <Button
          variant="grayedGhost"
          onClick={() => handleClick({ callback: handlePrevious })}
        >
          {t('PREVIOUS')}
        </Button>
      )}
      <Tooltip
        isDisabled={!isDisabled}
        label={t(
          'ADVISORY-REPORTS_CREATION-FLOW_CURRENT-INSURANCE_MODAL_NEXT-DISABLED',
        )}
      >
        <Button
          onClick={() => handleClick({ callback: handleSubmit })}
          isDisabled={isDisabled}
        >
          {isLastRiskObject
            ? t('SAVE-AND-CLOSE')
            : t(
                'ADVISORY-REPORTS_CREATION-FLOW_CURRENT-INSURANCE_MODAL_NEXT-COVERAGE',
              )}
        </Button>
      </Tooltip>
    </ModalFooter>
  );
};

export default AddInsuranceModalCoverageFooter;
